import { useRef, useState, type FC } from "react";
import { X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FileCard } from "@/shared/components/ui/file-card";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Separator } from "@/shared/components/ui/separator";
import { SheetTitle } from "@/shared/components/ui/sheet";
import { Tag } from "@/shared/components/ui/tag";
import ContactProfilePicture from "@/shared/components/Contact/ContactProfilePicture";
import DocumentSheet from "@/shared/components/document-viewer/DocumentSheet";
import { useContactPhotoUrl } from "@/shared/hooks/useContactPhotoUrl";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useContactForm } from "@/shared/hooks/useContactForm";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { saveProfilePicture } from "@/shared/utils/saveProfilePicture";
import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import {
  PERSON_STATUS_OPTIONS,
  ADDITIONAL_DETAIL_OPTIONS,
  ESTATE_ROLE_OPTIONS,
  PROFESSIONAL_ROLE_OPTIONS,
} from "@/shared/constants/contactDetailOptions";
import { parseAddress } from "@/shared/utils/addressUtils";
import { formatPhoneNumber } from "@/shared/utils/formatters";
import { sentryUtils } from "@/shared/utils/sentry";

import type { PersonContact } from "@/shared/types/estateContact";

import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { DetailSection, DetailRow } from "./contact-detail/DetailSection";
import { InviteCard } from "./contact-detail/InviteCard";
import { PersonalDetailsEdit } from "./contact-detail/PersonalDetailsEdit";
import { ContactInfoEdit } from "./contact-detail/ContactInfoEdit";
import { AdditionalDetailsEdit } from "./contact-detail/AdditionalDetailsEdit";
import { isProfessionalContact } from "@/shared/utils/contactUtils";
import { ProfessionalDetailsEdit } from "./contact-detail/ProfessionalDetailsEdit";

type EditingSection = "personal" | "professional" | "contact" | "additional" | null;

function getOptionLabel(
  options: ReadonlyArray<{ value: string; label: string }>,
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const option = options.find((o) => o.value === value);
  return option ? option.label : value;
}

function formatAddress(raw: string | null): string | null {
  if (!raw) return null;
  const parts = parseAddress(raw);
  const street = [parts.streetAddress, parts.apartment].filter(Boolean).join(", ");
  const cityStateZip = [parts.city, parts.state].filter(Boolean).join(", ");
  const secondLine = [cityStateZip, parts.zipCode].filter(Boolean).join(" ");
  if (!street && !secondLine) return null;
  return [street, secondLine].filter(Boolean).join("\n");
}

interface ContactDetailViewProps {
  contact: PersonContact;
  deceasedUserData?: {
    firstName?: string;
    displayName?: string;
    surName?: string;
  } | null;
  onClose: () => void;
  pendingInvitation?: { id: number; email: string } | null;
  acceptedInvitation?: { created_at: string } | null;
  onInvite?: (email: string, isAttorney?: boolean) => Promise<void>;
  onResendInvite?: (invitationId: number, email: string) => Promise<void>;
  onCancelInvite?: (invitationId: number) => Promise<void>;
  onRemoveAccess?: (email: string) => Promise<void>;
  isInviting?: boolean;
}

export const ContactDetailView: FC<ContactDetailViewProps> = ({
  contact,
  deceasedUserData,
  onClose,
  pendingInvitation,
  acceptedInvitation,
  onInvite,
  onResendInvite,
  onCancelInvite,
  onRemoveAccess,
  isInviting = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, person } = useAppContext();
  const { updateContactAsync, isUpdating, deleteContact, isDeleting } = useUserContacts();
  const { uploadProfilePicture } = useContactForm(person?.estate_id);
  const [localProfilePictureUrl, setLocalProfilePictureUrl] = useState<string | null>(null);
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [documentSheetOpen, setDocumentSheetOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    id: number;
    name: string;
    createdAt: Date;
  } | null>(null);

  const { photoUrl, isLoading: isLoadingPhoto } = useContactPhotoUrl(
    contact.profile_picture ? contact.id : null,
  );

  const fullName = [contact.first_name, contact.middle_name, contact.last_name]
    .filter(Boolean)
    .join(" ");
  const contactFirstName = contact.first_name || fullName;
  const deceasedFirstName =
    deceasedUserData?.firstName || deceasedUserData?.displayName || "the decedent";
  const isProfessional = isProfessionalContact(contact);
  const isCurrentUser =
    !!contact.email && !!user?.email && contact.email.toLowerCase() === user.email.toLowerCase();

  const originLabel =
    contact.extracted_from_document && contact.source_document
      ? `Extracted from ${contact.source_document.display_name || contact.source_document.file_name}`
      : isCurrentUser
        ? "You"
        : "Added by you";

  const relationshipLabel = contact.relationship
    ? getRelationshipLabel(contact.relationship)
    : null;
  const estateRoleLabel = contact.legal_role?.length
    ? contact.legal_role.map((r) => getOptionLabel(ESTATE_ROLE_OPTIONS, r) || r).join(", ")
    : null;
  const statusLabel = getOptionLabel(PERSON_STATUS_OPTIONS, contact.person_status);
  const additionalDetailsLabel = contact.additional_details?.length
    ? contact.additional_details
        .map((d) => getOptionLabel(ADDITIONAL_DETAIL_OPTIONS, d) || d)
        .join(", ")
    : null;
  const professionalRoleLabel = getOptionLabel(
    PROFESSIONAL_ROLE_OPTIONS,
    contact.professional_role,
  );
  const addressDisplay = formatAddress(contact.address);
  const phoneDisplay = contact.phone ? formatPhoneNumber(contact.phone) : null;

  const handleEditSection = (section: EditingSection) => {
    setEditingSection(section);
  };

  const handleSaveSection = async (updates: Record<string, unknown>) => {
    try {
      await updateContactAsync({ id: contact.id, ...updates });
      setEditingSection(null);
    } catch (error) {
      sentryUtils.captureError(error, "Failed to update contact");
    }
  };

  const handleProfilePictureSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLocalProfilePictureUrl(URL.createObjectURL(file));
    try {
      const fileKey = await uploadProfilePicture(file);
      if (fileKey) {
        await saveProfilePicture({ fileKey, contactId: contact.id, userId: contact.user_id });
        await updateContactAsync({ id: contact.id, profile_picture: fileKey });
        window.dispatchEvent(
          new CustomEvent("contactUpdated", {
            detail: { contactId: contact.id, hasProfilePicture: true },
          }),
        );
      }
    } catch (error) {
      sentryUtils.captureError(error, "Failed to upload profile picture");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-[16px] sm:p-[32px]">
        <IconButton
          className="absolute right-8 top-8"
          label="Close"
          tooltip={false}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </IconButton>

        {/* Header */}
        <div className="flex gap-6 items-center mb-6">
          <ContactProfilePicture
            profilePictureUrl={localProfilePictureUrl || photoUrl}
            isLoadingPhoto={isLoadingPhoto}
            fullName={fullName}
            onProfilePictureClick={() => fileInputRef.current?.click()}
            fileInputRef={fileInputRef}
            onProfilePictureSelect={handleProfilePictureSelect}
            containerClassName="mb-0"
          />
          <div className="flex flex-col gap-2 items-start">
            <SheetTitle className="text-[28px] font-semibold font-sans tracking-[-0.168px] leading-9">
              {fullName || "Unnamed contact"}
            </SheetTitle>
            <Tag color="stone" size="sm">
              {originLabel}
            </Tag>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {/* Invite card - hidden for current user */}
          {!isCurrentUser && (
            <InviteCard
              contactName={contactFirstName}
              contactEmail={contact.email}
              isInvited={!!contact.invited}
              deceasedFirstName={deceasedFirstName}
              pendingInvitation={pendingInvitation}
              onInvite={
                onInvite
                  ? (email) => onInvite(email, contact.relationship === "attorney")
                  : undefined
              }
              onResendInvite={onResendInvite}
              onCancelInvite={onCancelInvite}
              isInviting={isInviting}
            />
          )}

          {/* Personal details */}
          {editingSection === "personal" ? (
            <PersonalDetailsEdit
              contact={contact}
              deceasedFirstName={deceasedFirstName}
              onCancel={() => setEditingSection(null)}
              onSave={handleSaveSection}
              isSaving={isUpdating}
            />
          ) : (
            <DetailSection title="Personal details" onEdit={() => handleEditSection("personal")}>
              <DetailRow
                label="Full legal name"
                value={fullName || null}
                onAdd={() => handleEditSection("personal")}
              />
              <DetailRow
                label="Nickname"
                value={contact.nickname}
                onAdd={() => handleEditSection("personal")}
              />
              <DetailRow
                label={`Relationship to ${deceasedFirstName}`}
                value={relationshipLabel}
                onAdd={() => handleEditSection("personal")}
              />
              <DetailRow
                label="Estate role"
                value={estateRoleLabel}
                onAdd={() => handleEditSection("personal")}
              />
              <DetailRow
                label="Status"
                value={statusLabel}
                onAdd={() => handleEditSection("personal")}
              />
            </DetailSection>
          )}

          {/* Professional details */}
          {isProfessional &&
            (editingSection === "professional" ? (
              <ProfessionalDetailsEdit
                contact={contact}
                onCancel={() => setEditingSection(null)}
                onSave={handleSaveSection}
                isSaving={isUpdating}
              />
            ) : (
              <DetailSection
                title="Professional details"
                onEdit={() => handleEditSection("professional")}
              >
                <DetailRow
                  label="Professional role"
                  value={professionalRoleLabel}
                  onAdd={() => handleEditSection("professional")}
                />
                <DetailRow
                  label="Company or firm"
                  value={contact.company_or_firm}
                  onAdd={() => handleEditSection("professional")}
                />
              </DetailSection>
            ))}

          {/* Contact information */}
          {editingSection === "contact" ? (
            <ContactInfoEdit
              contact={contact}
              onCancel={() => setEditingSection(null)}
              onSave={handleSaveSection}
              isSaving={isUpdating}
            />
          ) : (
            <DetailSection title="Contact information" onEdit={() => handleEditSection("contact")}>
              <DetailRow
                label="Email"
                value={contact.email}
                onAdd={() => handleEditSection("contact")}
              />
              <DetailRow
                label="Cell phone"
                value={phoneDisplay}
                onAdd={() => handleEditSection("contact")}
              />
              <DetailRow
                label="Mailing address"
                value={addressDisplay}
                onAdd={() => handleEditSection("contact")}
              />
            </DetailSection>
          )}

          {/* Additional details */}
          {editingSection === "additional" ? (
            <AdditionalDetailsEdit
              contact={contact}
              onCancel={() => setEditingSection(null)}
              onSave={handleSaveSection}
              isSaving={isUpdating}
            />
          ) : (
            <DetailSection
              title="Additional details"
              onEdit={() => handleEditSection("additional")}
            >
              <DetailRow
                label="Additional details"
                value={additionalDetailsLabel}
                onAdd={() => handleEditSection("additional")}
              />
            </DetailSection>
          )}

          {/* Elayne access - visible when invite accepted */}
          {acceptedInvitation && (
            <div className="border border-primary rounded-lg">
              <div className="flex items-center p-4">
                <p className="flex-1 text-base font-semibold">Elayne access</p>
              </div>
              <Separator />
              <div className="p-4">
                <div className="flex gap-6 items-start w-full">
                  <p className="text-sm font-medium text-secondary w-[180px] shrink-0">Status</p>
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium">
                      Joined{" "}
                      {new Date(acceptedInvitation.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                    <Button
                      variant="link"
                      className="text-sm text-red-600 no-underline hover:underline p-0 h-auto justify-start"
                      onClick={() => setIsRevokeDialogOpen(true)}
                      disabled={!contact.email}
                    >
                      Revoke access
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents that mention [name] */}
          <div className="border border-primary rounded-lg">
            <div className="flex items-center p-4">
              <p className="text-base font-semibold">Documents that mention {contactFirstName}</p>
            </div>
            <Separator />
            <div className="flex flex-col gap-4 p-4">
              {contact.source_document ? (
                <FileCard
                  fileName={
                    contact.source_document.display_name || contact.source_document.file_name
                  }
                  subtitle={new Date(contact.source_document.created_at).toLocaleDateString(
                    "en-US",
                    { month: "short", day: "numeric", year: "numeric" },
                  )}
                  onClick={() => {
                    const createdDate = new Date(contact.source_document!.created_at);
                    if (!isNaN(createdDate.getTime())) {
                      setSelectedDocument({
                        id: contact.source_document!.id,
                        name:
                          contact.source_document!.display_name ||
                          contact.source_document!.file_name,
                        createdAt: createdDate,
                      });
                      setDocumentSheetOpen(true);
                    }
                  }}
                />
              ) : (
                <p className="text-sm font-medium text-tertiary">
                  None yet. As you upload documents, we'll automatically link the ones that mention{" "}
                  {contactFirstName}.
                </p>
              )}
            </div>
          </div>

          {/* Remove record footer - hidden for current user */}
          {!isCurrentUser && (
            <p className="text-sm text-secondary text-center w-full">
              Added {contactFirstName} by mistake?{" "}
              <Button
                variant="link"
                className="text-sm text-secondary no-underline hover:underline p-0 h-auto"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                Remove this record
              </Button>
            </p>
          )}

          <ConfirmDeleteDialog
            isOpen={isRevokeDialogOpen}
            onClose={() => setIsRevokeDialogOpen(false)}
            onConfirm={() => {
              if (contact.email) onRemoveAccess?.(contact.email);
              setIsRevokeDialogOpen(false);
            }}
            title={`Revoke ${contactFirstName}'s access`}
            description={`${contactFirstName} will no longer be able to view or manage this estate. Their contact record will remain.`}
          />
          <ConfirmDeleteDialog
            isOpen={isDeleteDialogOpen}
            onClose={() => setIsDeleteDialogOpen(false)}
            onConfirm={() => {
              deleteContact(contact.id);
              setIsDeleteDialogOpen(false);
              onClose();
            }}
            title={`Remove ${contactFirstName}`}
            description="This action is permanent and cannot be undone."
            isLoading={isDeleting}
          />
        </div>
      </div>

      {selectedDocument && (
        <DocumentSheet
          isOpen={documentSheetOpen}
          setIsOpen={setDocumentSheetOpen}
          documentId={selectedDocument.id}
          documentName={selectedDocument.name}
          documentCreatedAt={selectedDocument.createdAt}
        />
      )}
    </div>
  );
};
